<template>
  <div class="home-page">
    <div class="page-header">
      <div class="header-toolbar">
        <div class="header-left">
          <button
            type="button"
            class="menu-trigger-btn"
            aria-label="打开菜单"
            @click="sideMenuVisible = true"
          >
            <span class="menu-trigger-bar"></span>
            <span class="menu-trigger-bar"></span>
            <span class="menu-trigger-bar"></span>
          </button>
        </div>

        <div class="header-center">
          <div
            v-if="menuList.length"
            ref="menuScroll"
            class="header-menu-scroll"
          >
            <div class="header-menu-inner">
              <button
                v-for="item in menuList"
                :key="item.parentLabelCode"
                type="button"
                class="header-menu-label"
                :class="{ 'is-active': selectedParentLabelCode === item.parentLabelCode }"
                @click="onMenuLabelClick(item)"
              >
                {{ item.parentLabel }}
              </button>
            </div>
          </div>
        </div>

        <div class="header-right">
          <button
            type="button"
            class="search-btn"
            aria-label="打开搜索"
            @click="goToSearchPage"
          >
            <i class="el-icon-search"></i>
          </button>
        </div>
      </div>
    </div>
    <div class="page-body">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <div class="loading-text">加载中...</div>
      </div>
      
      <!-- 文本信息列表 -->
      <div v-else-if="textList.length > 0" class="text-list-wrapper">
        <div class="text-list">
          <!-- 左列 -->
          <div class="text-column left-column">
            <div 
              v-for="item in leftColumnData" 
              :key="item.id"
              class="text-card"
              @click="showCardDetail(item)"
              title="点击查看详情"
            >
              <!-- 标签区域 -->
              <div v-if="item.labels && item.labels.length > 0" class="card-labels">
                <span 
                  v-for="(label, labelIndex) in item.labels" 
                  :key="labelIndex"
                  class="label-tag"
                  :title="label"
                >
                  {{ truncateText(label, 10) }}
                </span>
              </div>
              
              <!-- 主题 -->
              <div v-if="item.theme" class="card-theme" :title="item.theme">
                <div
                  v-for="(line, li) in linesForCardPreview(item.theme, 20)"
                  :key="'t-l-' + li"
                  class="card-theme-line"
                >
                  {{ line }}
                </div>
              </div>
              
              <!-- 内容 -->
              <div
                v-if="item.summarized_text || item.summarizedText"
                class="card-content"
                :title="item.summarized_text || item.summarizedText"
              >
                <div
                  v-for="(line, li) in linesForCardPreview(item.summarized_text || item.summarizedText, 50)"
                  :key="'c-l-' + li"
                  class="card-content-line"
                >
                  {{ line }}
                </div>
              </div>
            </div>
          </div>
          
          <!-- 右列 -->
          <div class="text-column right-column">
            <div 
              v-for="item in rightColumnData" 
              :key="item.id"
              class="text-card"
              @click="showCardDetail(item)"
              title="点击查看详情"
            >
              <!-- 标签区域 -->
              <div v-if="item.labels && item.labels.length > 0" class="card-labels">
                <span 
                  v-for="(label, labelIndex) in item.labels" 
                  :key="labelIndex"
                  class="label-tag"
                  :title="label"
                >
                  {{ truncateText(label, 10) }}
                </span>
              </div>
              
              <!-- 主题 -->
              <div v-if="item.theme" class="card-theme" :title="item.theme">
                <div
                  v-for="(line, li) in linesForCardPreview(item.theme, 20)"
                  :key="'t-r-' + li"
                  class="card-theme-line"
                >
                  {{ line }}
                </div>
              </div>
              
              <!-- 内容 -->
              <div
                v-if="item.summarized_text || item.summarizedText"
                class="card-content"
                :title="item.summarized_text || item.summarizedText"
              >
                <div
                  v-for="(line, li) in linesForCardPreview(item.summarized_text || item.summarizedText, 50)"
                  :key="'c-r-' + li"
                  class="card-content-line"
                >
                  {{ line }}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 加载更多触发器 -->
        <div 
          v-if="hasMore" 
          ref="loadMoreTrigger" 
          class="load-more-trigger"
        ></div>
        
        <!-- 无感加载指示器（可选，非常小） -->
        <div v-if="loadingMore" class="loading-more-silent">
          <!-- 完全隐藏，或者显示一个非常小的点 -->
        </div>
        
        <!-- 没有更多数据（仅在数据加载完成后显示） -->
        <div v-if="!hasMore && textList.length > 0 && !loadingMore" class="no-more">
          <div class="no-more-text">没有更多数据了</div>
        </div>
      </div>
      
      <!-- 空状态 -->
      <div v-else class="empty-container">
        <div class="empty-text">暂无数据</div>
      </div>
    </div>

    <!-- 文本详情弹出卡片 -->
    <div v-if="showCardPopup" class="label-popup-mask" @click="hideCardDetail">
      <div class="label-popup-card" :class="{ flipped: popupFlipped }" @click.stop>
        <div class="label-popup-inner">
          <div class="label-popup-face label-popup-front">
          </div>

          <div class="label-popup-face label-popup-back">
            <div class="label-popup-header">
              <div class="label-popup-title">详情</div>
              <button class="label-popup-close" @click="hideCardDetail">×</button>
            </div>
            <div class="label-popup-content">
              <div v-if="detailLoading" class="popup-detail-loading">
                <div class="popup-detail-spinner"></div>
                <div class="popup-detail-loading-text">加载中...</div>
              </div>
              <template v-else>
              <div v-if="activeCardDetail && activeCardDetail.labels && activeCardDetail.labels.length > 0" class="popup-section">
                <div class="popup-section-title">标签</div>
                <div class="popup-labels">
                  <span 
                    v-for="(label, index) in activeCardDetail.labels" 
                    :key="index" 
                    class="label-tag popup-label-tag"
                  >
                    {{ label }}
                  </span>
                </div>
              </div>

              <div v-if="activeCardDetail && activeCardDetail.theme" class="popup-section">
                <div class="popup-section-title">主题</div>
                <div class="popup-section-text popup-lines">
                  <div
                    v-for="(line, li) in linesForDisplay(activeCardDetail.theme)"
                    :key="'pt-' + li"
                    class="popup-line"
                  >
                    {{ line }}
                  </div>
                </div>
              </div>

              <div v-if="activeCardDetail && activeCardDetail.content" class="popup-section">
                <div class="popup-section-title">内容</div>
                <div class="popup-section-text popup-lines">
                  <div
                    v-for="(line, li) in linesForDisplay(activeCardDetail.content)"
                    :key="'pc-' + li"
                    class="popup-line"
                  >
                    {{ line }}
                  </div>
                </div>
              </div>

              <div class="popup-section note-section" v-if="activeCardDetail">
                <div class="note-section-header">
                  <div class="popup-section-title note-title">笔记</div>
                  <button class="note-add-btn" @click.stop="openNoteEditor" title="编辑笔记">
                    <i class="el-icon-edit"></i>
                  </button>
                </div>
                <div v-if="currentCardNote" class="popup-section-text">{{ currentCardNote }}</div>
                <div v-else class="popup-empty">暂无笔记，点击编辑按钮记录一下吧</div>
              </div>

              <div 
                v-if="activeCardDetail && (!activeCardDetail.theme && !activeCardDetail.content && (!activeCardDetail.labels || activeCardDetail.labels.length === 0))" 
                class="popup-empty"
              >
                暂无详情内容
              </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showNoteEditor" class="note-editor-mask" @click="cancelNoteEditor">
        <div class="note-editor-panel" @click.stop>
          <div class="note-editor-title">编辑笔记</div>
          <textarea
            v-model="noteDraft"
            class="note-editor-textarea"
            placeholder="请输入笔记内容..."
            maxlength="1000"
          ></textarea>
          <div class="note-editor-actions">
            <button class="note-cancel-btn" @click.stop="cancelNoteEditor">取消</button>
            <button class="note-save-btn" @click.stop="saveCurrentNote" :disabled="noteSaving">
              {{ noteSaving ? '提交中...' : '确定' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <el-drawer
      title="导航"
      :visible.sync="sideMenuVisible"
      direction="ltr"
      :modal-append-to-body="true"
      size="260px"
      custom-class="home-side-drawer"
    >
      <nav class="side-menu-nav">
        <button
          type="button"
          class="side-menu-item"
          :class="{ active: $route.path === '/profile/week-report' }"
          @click="navFromMenu('/profile/week-report')"
        >
          <i class="el-icon-data-line"></i>
          <span>周报</span>
        </button>
      </nav>
    </el-drawer>
  </div>
</template>

<script>
import axios from 'axios'
import { fetchMenuList } from '@/api/homeMenuList'
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import { splitNumberedLines, splitNumberedLinesForPreview } from '@/utils/splitNumberedText'

export default {
  name: 'HomePage',
  data() {
    return {
      textList: [],
      loading: false,
      loadingMore: false,
      pageNum: 1,
      pageSize: 20,
      hasMore: true,
      observer: null,
      userId: null,
      theme: '',
      isRequesting: false, // 请求锁，防止并发请求
      showCardPopup: false,
      activeCardDetail: null,
      popupFlipped: false,
      popupFlipTimer: null,
      showNoteEditor: false,
      noteDraft: '',
      noteSaving: false,
      detailLoading: false,
      sideMenuVisible: false,
      menuList: [],
      selectedParentLabelCode: null
    }
  },
  async mounted() {
    await this.loadMenuList()
    if (this.routeParentLabelCodeTrimmed()) {
      this.selectedParentLabelCode = this.routeParentLabelCodeTrimmed()
      this.theme = ''
    } else {
      this.applyDefaultMenuSelection()
    }
    await this.$nextTick()
    if (this.routeParentLabelCodeTrimmed()) {
      this.scrollMenuActiveIntoView()
    } else {
      this.scrollMenuToEnd()
    }

    this.userId = this.getUserId()
    if (!this.userId) {
      this.$message.error('请先登录')
      return
    }
    this.fetchTextList(false)
    this.$nextTick(() => {
      this.initIntersectionObserver()
    })
    // 监听刷新事件
    this.$bus.$on('refresh-home-page', this.refreshAndScrollToTop)
  },
  activated() {
    this.$nextTick(() => this.scrollMenuToEnd())
  },
  beforeDestroy() {
    this.destroyObserver()
    if (this.popupFlipTimer) {
      clearTimeout(this.popupFlipTimer)
      this.popupFlipTimer = null
    }
    // 移除事件监听
    this.$bus.$off('refresh-home-page', this.refreshAndScrollToTop)
  },
  methods: {
    getUserId() {
      return localStorage.getItem('userId') || null
    },
    navFromMenu(path) {
      this.sideMenuVisible = false
      if (this.$route.path !== path) {
        this.$router.push(path)
      }
    },
    goToSearchPage() {
      this.$router.push('/search')
    },
    /** 路由 ?parentLabelCode= 与列表选中同步（内容提取页跳转） */
    routeParentLabelCodeTrimmed() {
      const q = this.$route.query.parentLabelCode
      if (q == null || q === '') return ''
      const s = String(q).trim()
      return s || ''
    },
    async loadMenuList() {
      try {
        this.menuList = await fetchMenuList()
      } catch (e) {
        console.error(e)
        this.menuList = []
      }
    },
    /** 首次进入：默认选中「右侧第一个」即列表最后一项（与滚动到最右一致） */
    applyDefaultMenuSelection() {
      if (!this.menuList.length) return
      const last = this.menuList[this.menuList.length - 1]
      if (!last || !last.parentLabelCode) return
      this.selectedParentLabelCode = last.parentLabelCode
      this.theme = ''
    },
    /** 默认展示最右侧标签（滚动到最右） */
    scrollMenuToEnd() {
      const el = this.$refs.menuScroll
      if (!el || typeof el.scrollLeft !== 'number') return
      el.scrollLeft = Math.max(0, el.scrollWidth - el.clientWidth)
    },
    /** 将当前选中的一级分类滚入顶栏可视区（从内容提取带参进入时避免仍停在最右侧） */
    scrollMenuActiveIntoView() {
      this.$nextTick(() => {
        const code = this.selectedParentLabelCode
        const scrollEl = this.$refs.menuScroll
        if (!code || !scrollEl || typeof scrollEl.scrollLeft !== 'number') return
        const idx = this.menuList.findIndex((x) => x.parentLabelCode === code)
        if (idx < 0) return
        const inner = scrollEl.querySelector('.header-menu-inner')
        if (!inner || !inner.children[idx]) return
        const btn = inner.children[idx]
        if (typeof btn.scrollIntoView === 'function') {
          btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
        }
      })
    },
    /** 点击分类：列表接口传 parentLabelCode，不使用 theme 承载分类名 */
    onMenuLabelClick(item) {
      if (!item || !this.userId) {
        if (!this.userId) this.$message.error('请先登录')
        return
      }
      if (this.selectedParentLabelCode === item.parentLabelCode) {
        this.selectedParentLabelCode = null
        this.theme = ''
        this.refreshAndScrollToTop()
        return
      }
      this.selectedParentLabelCode = item.parentLabelCode
      this.theme = ''
      this.pageNum = 1
      this.hasMore = true
      this.fetchTextList(false)
      this.scrollMenuActiveIntoView()
    },
    truncateText(text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },
    linesForDisplay(text) {
      return splitNumberedLines(text)
    },
    /** 列表卡片：分条展示，但全文总字数不超过 maxTotal（与原先单行 truncate 一致） */
    linesForCardPreview(text, maxTotal) {
      return splitNumberedLinesForPreview(text, maxTotal)
    },
    // 深拷贝数据，确保完全隔离
    deepClone(obj) {
      if (obj === null || typeof obj !== 'object') return obj
      if (obj instanceof Date) return new Date(obj.getTime())
      if (obj instanceof Array) return obj.map(item => this.deepClone(item))
      if (typeof obj === 'object') {
        const cloned = {}
        for (const key in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
            cloned[key] = this.deepClone(obj[key])
          }
        }
        return cloned
      }
      return obj
    },
    async fetchTextList(isLoadMore = false) {
      if (!this.userId) {
        this.$message.error('请先登录')
        return
      }
      // 请求锁：防止并发请求
      if (this.isRequesting) {
        console.log('[分页加载] 请求进行中，忽略重复请求')
        return
      }

      if (isLoadMore) {
        if (!this.hasMore) {
          console.log('[分页加载] 没有更多数据')
          return
        }
        this.loadingMore = true
      } else {
        this.loading = true
        this.pageNum = 1
        this.textList = []
        this.hasMore = true
      }

      // 确定当前要请求的页码
      const currentPage = this.pageNum
      console.log(`[分页加载] ${isLoadMore ? '加载更多' : '首次加载'}, 页码: ${currentPage}`)

      // 设置请求锁
      this.isRequesting = true

      try {
        const requestData = {
          theme: this.theme || '',
          parentLabelCode: this.selectedParentLabelCode || '',
          pageNum: currentPage,
          pageSize: this.pageSize
        }
        const response = await axios.post('/api/app/core/text-summary/list', requestData)
        
        // 统一数据提取逻辑
        let newData = []
        const data = response.data
        if (Array.isArray(data)) {
          newData = data
        } else if (data?.data && Array.isArray(data.data)) {
          newData = data.data
        } else if (data?.list && Array.isArray(data.list)) {
          newData = data.list
        } else if (data?.records && Array.isArray(data.records)) {
          newData = data.records
          if (data.total !== undefined) {
            this.hasMore = data.total > currentPage * this.pageSize
          }
        } else {
          console.warn('接口返回数据格式不符合预期:', data)
          newData = []
        }

        // 深拷贝 + 字段映射 + 生成唯一ID + 数据去重
        const existingIds = new Set(this.textList.map(item => item._uniqueId))
        const normalizedData = []
        const newDataIds = new Set() // 新数据内部去重
        
        for (let i = 0; i < newData.length; i++) {
          const item = newData[i]
          // 深拷贝，确保完全隔离
          const cloned = this.deepClone(item)
          
          // 统一字段名：content -> summarized_text
          if (cloned.content && !cloned.summarized_text) {
            cloned.summarized_text = cloned.content
          }
          
          // 生成唯一ID
          cloned._uniqueId = item.id;
          
          // 双重去重：1. 与现有数据去重 2. 新数据内部去重
          if (!existingIds.has(cloned._uniqueId) && !newDataIds.has(cloned._uniqueId)) {
            existingIds.add(cloned._uniqueId)
            newDataIds.add(cloned._uniqueId)
            normalizedData.push(cloned)
          } else {
            console.warn(`[分页加载] 发现重复数据，已跳过: ${cloned._uniqueId}`)
          }
        }

        // 原子性更新：创建新数组，不修改现有数组的引用
        if (isLoadMore) {
          // 使用展开运算符创建新数组，确保Vue能正确追踪变化
          const newList = this.textList.slice() // 先复制现有数组
          newList.push(...normalizedData) // 追加新数据
          this.textList = newList // 一次性替换
        } else {
          this.textList = normalizedData
        }

        // 判断是否还有更多数据
        if (normalizedData.length < this.pageSize) {
          this.hasMore = false
        }

        // 请求成功后，递增页码
        this.pageNum = currentPage + 1
        console.log(`[分页加载] 请求成功，数据量: ${normalizedData.length}, 下次页码: ${this.pageNum}, 还有更多: ${this.hasMore}`)
      } catch (error) {
        console.error('获取文本列表失败:', error)
        // 请求失败时，不更新页码，下次重试同一页
        if (!isLoadMore) {
          this.textList = []
        }
      } finally {
        this.loading = false
        this.loadingMore = false
        this.isRequesting = false // 释放请求锁
      }
    },
    initIntersectionObserver() {
      if (!window.IntersectionObserver) {
        console.warn('浏览器不支持 IntersectionObserver，将使用滚动事件')
        this.initScrollListener()
        return
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && this.hasMore && !this.isRequesting) {
              this.fetchTextList(true)
            }
          })
        },
        {
          root: null,
          rootMargin: '1000px', // 增大提前加载距离，实现无感刷新
          threshold: 0.1
        }
      )

      this.$nextTick(() => {
        this.observeTrigger()
      })
    },
    initScrollListener() {
      const handleScroll = () => {
        if (this.isRequesting || !this.hasMore) return

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const windowHeight = window.innerHeight
        const documentHeight = document.documentElement.scrollHeight

        // 提前300px触发加载，实现无感刷新
        if (scrollTop + windowHeight >= documentHeight - 1000) {
          this.fetchTextList(true)
        }
      }

      window.addEventListener('scroll', handleScroll)
      this.scrollHandler = handleScroll
    },
    observeTrigger() {
      if (!this.observer) return
      
      const trigger = this.$refs.loadMoreTrigger
      if (trigger) {
        const element = Array.isArray(trigger) ? trigger[0] : trigger
        if (element) {
          // 先取消观察，避免重复观察
          this.observer.unobserve(element)
          this.observer.observe(element)
        }
      }
    },
    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler)
        this.scrollHandler = null
      }
    },
    // 刷新列表并回到顶部
    refreshAndScrollToTop() {
      // 回到顶部
      this.scrollToTop()
      // 刷新列表
      this.fetchTextList(false)
    },
    // 滚动到顶部
    scrollToTop() {
      // 优先滚动内容区域（MainPage的content-area）
      const contentArea = document.querySelector('.content-area')
      if (contentArea) {
        contentArea.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      } else {
        // 如果没有找到content-area，则滚动window
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      }
    },
    async showCardDetail(item) {
      if (!item) return
      if (!this.userId) {
        this.$message.error('请先登录')
        return
      }
      const recordId = item.id !== undefined && item.id !== null ? String(item.id) : (item._uniqueId ? String(item._uniqueId) : '')
      if (!recordId) {
        this.$message.warning('无法获取内容ID')
        return
      }
      this.activeCardDetail = { key: recordId }
      this.showCardPopup = true
      this.popupFlipped = false
      this.detailLoading = true
      if (this.popupFlipTimer) {
        clearTimeout(this.popupFlipTimer)
        this.popupFlipTimer = null
      }
      this.popupFlipTimer = setTimeout(() => {
        this.popupFlipped = true
      }, 60)
      try {
        const res = await axios.post('/api/app/core/text-summary/detail', {
          id: recordId
        })
        const data = res?.data ?? {}
        const labels = Array.isArray(data.labels) ? data.labels : (data.labels ? [String(data.labels)] : [])
        this.activeCardDetail = {
          key: recordId,
          labels,
          theme: data.theme || '',
          content: data.summarized_text || data.summarizedText || data.content || '',
          noteContent: data.noteContent ?? data.note ?? data.remark ?? data.memo ?? ''
        }
      } catch (error) {
        console.error('获取详情失败:', error)
        this.$message.error(getApiErrorMessage(error, '获取详情失败，请重试'))
        this.hideCardDetail()
      } finally {
        this.detailLoading = false
      }
    },
    hideCardDetail() {
      this.showCardPopup = false
      this.activeCardDetail = null
      this.detailLoading = false
      this.popupFlipped = false
      this.showNoteEditor = false
      this.noteDraft = ''
      if (this.popupFlipTimer) {
        clearTimeout(this.popupFlipTimer)
        this.popupFlipTimer = null
      }
    },
    openNoteEditor() {
      this.noteDraft = this.currentCardNote
      this.showNoteEditor = true
    },
    cancelNoteEditor() {
      if (this.noteSaving) return
      this.showNoteEditor = false
      this.noteDraft = ''
    },
    async saveCurrentNote() {
      if (!this.userId) {
        this.$message.error('请先登录')
        return
      }
      if (!this.activeCardDetail || !this.activeCardDetail.key) {
        this.$message.warning('未获取到内容ID，无法提交笔记')
        return
      }
      const noteContent = (this.noteDraft || '').trim()
      if (!noteContent) {
        this.$message.warning('请输入笔记内容')
        return
      }
      if (this.noteSaving) return

      this.noteSaving = true
      try {
        const res = await axios.post('/api/app/core/note/add', {
          idEzTextSummary: this.activeCardDetail.key,
          noteContent
        })
        const savedContent = res?.data?.noteContent ?? noteContent
        this.activeCardDetail.noteContent = savedContent
        const item = this.textList.find(i => String(i.id) === this.activeCardDetail.key)
        if (item) {
          this.$set(item, 'noteContent', savedContent)
        }
        this.showNoteEditor = false
        this.noteDraft = ''
        this.$message.success('笔记保存成功')
      } catch (error) {
        console.error('保存笔记失败:', error)
        this.$message.error(getApiErrorMessage(error, '保存笔记失败，请重试'))
      } finally {
        this.noteSaving = false
      }
    }
  },
  computed: {
    currentCardNote() {
      return (this.activeCardDetail && this.activeCardDetail.noteContent) || ''
    },
    // 左列数据：索引为偶数的项（0, 2, 4, ...）
    leftColumnData() {
      return this.textList.filter((item, index) => index % 2 === 0)
    },
    // 右列数据：索引为奇数的项（1, 3, 5, ...）
    rightColumnData() {
      return this.textList.filter((item, index) => index % 2 === 1)
    }
  },
  watch: {
    textList: {
      handler() {
        // 数据更新后，重新观察触发器（仅在加载更多时）
        if (this.loadingMore === false && this.hasMore) {
          this.$nextTick(() => {
            this.observeTrigger()
          })
        }
      },
      deep: false // 不需要深度监听，因为我们冻结了对象
    },
    '$route.query.parentLabelCode'(val) {
      if (val == null || String(val).trim() === '') return
      const code = String(val).trim()
      if (this.selectedParentLabelCode === code) {
        this.scrollMenuActiveIntoView()
        return
      }
      this.selectedParentLabelCode = code
      this.theme = ''
      if (this.userId) {
        this.pageNum = 1
        this.hasMore = true
        this.fetchTextList(false)
      }
      this.scrollMenuActiveIntoView()
    }
  }
}
</script>

<style scoped>
.home-page {
  min-height: 100%;
  background: #f5f5f5;
}

.page-header {
  background: #fff;
  border-bottom: 1px solid #e5e5e5;
  position: sticky;
  top: 0;
  z-index: 10;
}

.header-toolbar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  gap: 8px;
}

.header-left {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 36px;
}

/* 中间：仅做水平方向占位与居中，标签区可横向滑动 */
.header-center {
  margin-top: 5px;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-menu-scroll {
  width: 100%;
  max-width: 100%;
  display: flex;
  justify-content: flex-start;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 2px 0 4px;
  box-sizing: border-box;
}

.header-menu-scroll::-webkit-scrollbar {
  display: none;
}

.header-menu-inner {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  min-width: min-content;
}

.header-menu-label {
  flex-shrink: 0;
  margin: 0;
  padding: 2px 4px 4px;
  border: none;
  background: none;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.75);
  cursor: pointer;
  line-height: 1.35;
  white-space: nowrap;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  transition: color 0.2s;
}

.header-menu-label:hover {
  color: #1890ff;
}

.header-menu-label.is-active {
  color: #1890ff;
  font-weight: 500;
  border-bottom-color: #1890ff;
  background: none;
}

.header-right {
  flex: 0 0 auto;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
}

.menu-trigger-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  color: #000000;
  transition: background 0.2s, color 0.2s;
  flex-shrink: 0;
}

.menu-trigger-btn:hover {
  background: rgba(24, 144, 255, 0.08);
  color: #1890ff;
}

.menu-trigger-bar {
  display: block;
  width: 18px;
  height: 2px;
  border-radius: 1px;
  background: currentColor;
}

/* 搜索按钮 */
.search-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 12;
  flex-shrink: 0;
  width: 36px; /* 固定宽度，用于计算搜索框位置 */
  height: 36px;
}

.search-btn:hover {
  background: #f5f5f5;
}

.search-btn i {
  font-size: 18px;
}

.page-body {
  padding: 16px;
  min-height: calc(100vh - 120px);
}

/* 文本列表包装器 */
.text-list-wrapper {
  width: 100%;
}

/* 文本列表 */
.text-list {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
}

/* 文本列 */
.text-column {
  flex: 1;
  min-width: 0; /* 防止flex子项溢出 */
  display: flex;
  flex-direction: column;
}

/* 文本卡片 */
.text-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s;
  break-inside: avoid;
  margin-bottom: 12px;
  display: block;
  cursor: pointer;
}

.text-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* 标签区域 */
.card-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.label-tag {
  display: inline-block;
  padding: 4px 12px;
  background: #e6f7ff;
  color: #1890ff;
  border-radius: 12px;
  font-size: 12px;
  line-height: 1.5;
  cursor: pointer;
  transition: all 0.2s;
}

.label-tag:hover {
  background: #bae7ff;
}

.label-popup-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.label-popup-card {
  width: min(560px, 100%);
  height: min(70vh, 560px);
  perspective: 1200px;
}

.label-popup-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.55s ease;
  transform-style: preserve-3d;
}

.label-popup-card.flipped .label-popup-inner {
  transform: rotateY(180deg);
}

.label-popup-face {
  position: absolute;
  inset: 0;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  backface-visibility: hidden;
  overflow: hidden;
}

.label-popup-front {
  background: linear-gradient(135deg, #1890ff, #36cfc9);
}

.label-popup-back {
  background: #fff;
  transform: rotateY(180deg);
  display: flex;
  flex-direction: column;
}

.label-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.label-popup-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.label-popup-close {
  border: none;
  background: transparent;
  color: #999;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}

.label-popup-close:hover {
  color: #666;
}

.label-popup-content {
  padding: 16px;
  font-size: 14px;
  color: #555;
  line-height: 1.7;
  word-break: break-word;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.popup-section + .popup-section {
  margin-top: 14px;
}

.popup-section-title {
  font-size: 13px;
  color: #999;
  margin-bottom: 8px;
}

.popup-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.popup-label-tag {
  cursor: default;
}

.popup-section-text {
  color: #444;
  white-space: pre-wrap;
}

.popup-lines {
  white-space: normal;
}

.popup-lines .popup-line {
  display: block;
  line-height: 1.65;
  margin-bottom: 10px;
  word-break: break-word;
}

.popup-lines .popup-line:last-child {
  margin-bottom: 0;
}

.popup-empty {
  color: #999;
}

.popup-detail-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
}

.popup-detail-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e8e8e8;
  border-top-color: #1890ff;
  border-radius: 50%;
  animation: popup-spin 0.8s linear infinite;
}

.popup-detail-loading-text {
  margin-top: 12px;
  font-size: 14px;
}

@keyframes popup-spin {
  to {
    transform: rotate(360deg);
  }
}

.note-section {
  border-top: 1px dashed #e8e8e8;
  padding-top: 12px;
}

.note-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.note-title {
  margin-bottom: 0;
}

.note-add-btn {
  border: none;
  background: #f5f7fa;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  padding: 0;
  font-size: 14px;
  line-height: 24px;
  cursor: pointer;
}

.note-add-btn:hover {
  background: #e6f7ff;
}

.note-add-btn i {
  font-size: 14px;
  color: #409eff;
}

.note-editor-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.note-editor-panel {
  width: min(360px, calc(100vw - 56px));
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
}

.note-editor-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.note-editor-textarea {
  width: 100%;
  min-height: 90px;
  max-height: 180px;
  resize: vertical;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.6;
  outline: none;
  box-sizing: border-box;
}

.note-editor-textarea:focus {
  border-color: #1890ff;
}

.note-editor-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.note-cancel-btn,
.note-save-btn {
  border: none;
  border-radius: 14px;
  padding: 5px 12px;
  font-size: 12px;
  cursor: pointer;
}

.note-cancel-btn {
  background: #f5f5f5;
  color: #666;
}

.note-save-btn {
  background: #1890ff;
  color: #fff;
}

.note-save-btn:hover {
  background: #40a9ff;
}

.note-save-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

@keyframes labelPopupIn {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 主题 */
.card-theme {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
  line-height: 1.5;
}

.card-theme-line + .card-theme-line {
  margin-top: 4px;
}

/* 内容 */
.card-content {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  word-wrap: break-word;
}

.card-content-line + .card-content-line {
  margin-top: 6px;
}

/* 加载状态 */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.loading-text {
  color: #999;
  font-size: 14px;
}

/* 空状态 */
.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
}

.empty-text {
  color: #999;
  font-size: 14px;
}

/* 加载更多触发器 */
.load-more-trigger {
  height: 1px;
  width: 100%;
  visibility: hidden;
  margin-top: 20px;
}

/* 无感加载指示器（完全隐藏） */
.loading-more-silent {
  height: 0;
  width: 100%;
  visibility: hidden;
  overflow: hidden;
}

/* 没有更多数据 */
.no-more {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  width: 100%;
}

.no-more-text {
  color: #999;
  font-size: 12px;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .header-toolbar {
    padding: 14px 16px;
  }

  .page-header h2 {
    font-size: 16px;
  }

  .page-body {
    padding: 12px;
  }

  .text-list {
    gap: 8px;
  }

  .text-column {
    gap: 0;
  }

  .text-card {
    padding: 12px;
    border-radius: 6px;
    margin-bottom: 8px;
  }

  .card-labels {
    gap: 6px;
    margin-bottom: 8px;
  }

  .label-tag {
    padding: 3px 8px;
    font-size: 11px;
  }

  .card-theme {
    font-size: 14px;
    margin-bottom: 6px;
  }

  .card-content {
    font-size: 12px;
    line-height: 1.5;
  }

  .label-popup-card {
    border-radius: 10px;
    height: min(72vh, 540px);
  }

  .label-popup-header {
    padding: 12px 14px;
  }

  .label-popup-content {
    padding: 14px;
    font-size: 13px;
  }

  .note-editor-panel {
    width: calc(100vw - 64px);
    padding: 10px;
  }

  .note-editor-textarea {
    min-height: 80px;
    font-size: 13px;
  }
}
</style>

<style>
/* Drawer 挂载在 body；与首页顶栏一致：白底 + 灰线分割 + 蓝色强调 */
.home-side-drawer.el-drawer {
  background: #ffffff !important;
  border-right: 1px solid #e5e5e5 !important;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06) !important;
}

.home-side-drawer .el-drawer__header {
  margin-bottom: 0 !important;
  padding: 16px 20px !important;
  background: #ffffff !important;
  border-bottom: 1px solid #e5e5e5 !important;
  color: rgba(0, 0, 0, 0.85) !important;
  font-size: 16px;
  font-weight: 500;
}

.home-side-drawer .el-drawer__close-btn {
  color: rgba(0, 0, 0, 0.45) !important;
}

.home-side-drawer .el-drawer__close-btn:hover {
  color: #1890ff !important;
}

.home-side-drawer .el-drawer__body {
  padding: 16px !important;
  background: #f5f5f5 !important;
}

.side-menu-nav {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.side-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #ffffff;
  color: rgba(0, 0, 0, 0.65);
  font-size: 15px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
  box-sizing: border-box;
}

.side-menu-item i {
  font-size: 18px;
  color: #1890ff;
}

.side-menu-item:hover {
  background: #e6f7ff;
  border-color: #91d5ff;
  color: #1890ff;
}

.side-menu-item:hover i {
  color: #1890ff;
}

.side-menu-item.active {
  background: #e6f7ff;
  border-color: #1890ff;
  color: #1890ff;
}

.side-menu-item.active i {
  color: #1890ff;
}
</style>

